import { usePageContext } from '@internal/page/PageContext'
import { Machine } from '@internal/utils/machine/Machine'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import {
  Checkbox,
  FormControlLabel,
  Slide,
  Typography,
} from '@renderer-ui-library/mui'
import { MachineComparisonContext } from 'blocks/SearchResultsBlock/CompareMachines/MachineComparisonContext'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React, { ChangeEvent, useCallback, useContext } from 'react'
import { formatNumber } from 'utils/numbers/formatNumber'
import { SerpCard } from '../SerpCard'
import styles from './productCard.module.scss'

type Props = {
  machine: Machine
}

export const ProductCard: React.FC<Props> = React.memo(({ machine }) => {
  const { locale } = usePageContext()
  const { t } = useTranslation(localeNamespace.common)
  const {
    isCompareModeEnabled,
    isSelectedForComparison,
    addMachine,
    removeMachine,
  } = useContext(MachineComparisonContext)

  const handleCompareCheckboxChange = useCallback(
    (_: ChangeEvent, checked: boolean) => {
      if (checked) {
        addMachine(machine)
      } else {
        removeMachine(machine.machineGroupId)
      }
    },
    [addMachine, machine, removeMachine]
  )

  return (
    <div className={styles.wrapper}>
      <Slide
        direction='down'
        in={isCompareModeEnabled}
        mountOnEnter
        unmountOnExit
      >
        <div className={styles.checkBoxWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelectedForComparison(machine)}
                onChange={handleCompareCheckboxChange}
              />
            }
            label={t(translations.compareMachinesSearchResultLabel)}
          />
        </div>
      </Slide>

      <SerpCard machine={machine}>
        <div>
          <Typography variant='body2' color='textSecondary' component='div'>
            <div className={styles.highlightWrapper}>
              {!!machine.yearOfProduction && (
                <div className={styles.highlight}>
                  <Typography variant='body1' fontWeight={600}>
                    {t(translations.searchResultsCardYear)}
                  </Typography>
                  <Typography variant='body2'>
                    {machine.yearOfProduction}
                  </Typography>
                </div>
              )}
              {!!machine.enginePower && (
                <div className={styles.highlight}>
                  <Typography variant='body1' fontWeight={600}>
                    {t(translations.searchResultsCardHorsePower)}
                  </Typography>
                  <Typography variant='body2'>
                    {`${formatNumber(locale, machine.enginePower)} ${t(
                      translations.enginePowerUnitHP
                    )}`}
                  </Typography>
                </div>
              )}
              {!!machine.engineHours && (
                <div className={styles.highlight}>
                  <Typography variant='body1' fontWeight={600}>
                    {t(translations.searchResultsCardHours)}
                  </Typography>
                  <Typography variant='body2'>
                    {`${formatNumber(locale, machine.engineHours)}`}
                  </Typography>
                </div>
              )}
            </div>
          </Typography>
        </div>
      </SerpCard>
    </div>
  )
})

ProductCard.displayName = 'ProductCard'
